import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { makeGetSortedActiveProjectTaskGroups } from 'selectors/task-groups';
import { makeGetFetchedState, makeGetLoadingState, makeGetErrorState } from 'selectors/global';

import { fetchTaskGroups } from 'actions/task-groups';

import ResourceSelector from 'components/ResourceSelector';
import Select from 'components/select';

const TaskGroupSelector = ({ taskGroups, excluded, selected, projectId, fetched, loading, errored, itemComponent, useSelect, autoSelectFirst, onSelect }) => {
    const { t } = useTranslation('task');

    const attemptFetch = useCallback(() => {
        if (!errored && !fetched && !loading && projectId) {
            fetchTaskGroups(projectId);
        }
    }, [errored, fetched, loading, projectId]);

    useEffect(() => {
        attemptFetch();
    }, [attemptFetch]);

    const availableTaskGroups = useMemo(() => {
        if (excluded.length) {
            return taskGroups.filter(({ id }) => !excluded.includes(id));
        }

        return taskGroups;
    }, [taskGroups, excluded]);

    useEffect(() => {
        if (autoSelectFirst && !selected && availableTaskGroups.length) {
            onSelect({
                id: availableTaskGroups[0].id
            });
        }
    }, [autoSelectFirst, selected, availableTaskGroups]);

    if (useSelect) {
        return (
            <Select
                placeholder={ t('Select a status') }
                withSearch={true}
                emptyText={ t('noEntries.noLists') }
                options={availableTaskGroups.map(taskGroup => ({ id: taskGroup.id, text: taskGroup.name }))}
                selected={selected}
                onChange={onSelect}
            />
        )
    }

    return (
        <ResourceSelector
            withSearch={true}
            loading={projectId && !fetched}
            searchPlaceholder={ t('Search statuses') }
            emptyText={ t('noEntries.noLists') }
            items={availableTaskGroups.map(taskGroup => ({
                id: taskGroup.id,
                title: taskGroup.name
            }))}
            selected={selected}
            itemComponent={itemComponent}
            onSelect={onSelect}
        />
    );
}

TaskGroupSelector.defaultProps = {
    excluded: []
}

const makeMapStateToProps = () => {
    const getSortedActiveProjectTaskGroups = makeGetSortedActiveProjectTaskGroups();
    const getLoading = makeGetLoadingState();
    const getFetched = makeGetFetchedState();
    const getErrored = makeGetErrorState();

    return ({ taskGroups }, ownProps) => ({
        taskGroups: getSortedActiveProjectTaskGroups(taskGroups.items, ownProps.projectId),
        loading: getLoading(taskGroups.fetchInfo, 'project', ownProps.projectId),
        fetched: getFetched(taskGroups.fetchInfo, 'project', ownProps.projectId),
        errored: getErrored(taskGroups.fetchInfo, 'project', ownProps.projectId)
    });
}

export default connect(makeMapStateToProps)(TaskGroupSelector);
